import type { PartnerCriteria } from '@houzy/schemas/partner-cockpit/company';
import { partnerMatchingLanguageSchema, type PartnerMatchingLanguage } from '@houzy/schemas/partner-management';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import { useId, useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { setsEqual } from '../../../lib/sets';
import { useSavePartnerCriteria } from '../api/partnerCriteriaApi';
import { MATCHING_LANGUAGE_LABEL_KEYS } from '../labels';
import { useDirtyForm } from '../state/useDirtyForm';
import { SettingsCard } from './SettingsCard';

export type PartnerCriteriaCardProps = {
    criteria: PartnerCriteria;
};

const LANGUAGE_OPTIONS = partnerMatchingLanguageSchema.options;

const LANGUAGE_OPTION_SET: ReadonlySet<string> = new Set(LANGUAGE_OPTIONS);

function isMatchingLanguage(value: string): value is PartnerMatchingLanguage {
    return LANGUAGE_OPTION_SET.has(value);
}

export function PartnerCriteriaCard({ criteria }: PartnerCriteriaCardProps): ReactElement {
    const { t } = useTranslation();
    const languagesLabelId = useId();
    const saveMutation = useSavePartnerCriteria();

    const savedLanguages = useMemo<ReadonlySet<PartnerMatchingLanguage>>(
        () => new Set(criteria.matchingLanguages),
        [criteria.matchingLanguages],
    );
    const { value, setValue, reset, isDirty } = useDirtyForm(savedLanguages, setsEqual);

    const selectedLanguages = LANGUAGE_OPTIONS.filter((language) => value.has(language));

    const handleLanguagesChange = (event: SelectChangeEvent<PartnerMatchingLanguage[]>): void => {
        const next: readonly string[] =
            typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
        setValue(new Set(next.filter(isMatchingLanguage)));
    };

    const handleSave = (): void => {
        saveMutation.mutate({ matchingLanguages: selectedLanguages });
    };

    const languagesLabel = t('SETTINGS.COMPANY.CRITERIA.LANGUAGES');

    return (
        <SettingsCard
            title={t('SETTINGS.COMPANY.CRITERIA.TITLE')}
            dirty={isDirty}
            saving={saveMutation.isPending}
            onSave={handleSave}
            onReset={reset}>
            <FormControl fullWidth>
                <InputLabel id={languagesLabelId}>{languagesLabel}</InputLabel>
                <Select<PartnerMatchingLanguage[]>
                    labelId={languagesLabelId}
                    multiple
                    value={selectedLanguages}
                    onChange={handleLanguagesChange}
                    input={<OutlinedInput label={languagesLabel} />}
                    renderValue={(languages) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {languages.map((language) => (
                                <Chip key={language} size="small" label={t(MATCHING_LANGUAGE_LABEL_KEYS[language])} />
                            ))}
                        </Box>
                    )}>
                    {LANGUAGE_OPTIONS.map((language) => (
                        <MenuItem key={language} value={language}>
                            <Checkbox checked={value.has(language)} sx={{ py: 0 }} />
                            <ListItemText primary={t(MATCHING_LANGUAGE_LABEL_KEYS[language])} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </SettingsCard>
    );
}
